import React, { useState, useRef, useEffect } from "react";
import AdminHOC from "../../../components/shared/HOC/AdminHOC";
import { Row, Col } from "antd";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DownloadOutlined } from "@ant-design/icons";
import CustomTab from "../../../components/shared/tab/TabComponent";
import searchIconsvg from "../../../assets/icons/Search-Icon.svg";
import usePagination from "../../../assets/hooks/PaginationHooks/usePagination";
import CustomButton from "../../../components/global/Button/CustomButton";
import CustomInput from "../../../components/global/CustomInput/CustomInput";
import CommonTable from "../../../components/shared/table/Table";
import LottieIcon from "../../../components/shared/lottieIcon/LottieIcon";
import { showErrorMessage, showSuccessMessage } from "../../../components/shared/pagination/messages";
import { courseReportColumns, columnMapping } from "../../../assets/FormFeilds/CourseAdminAllReport";
import { useCourseReport } from "../../../hooks/CoursesHooks/useCourseRport";
import { downloadCourseKpiReport } from "../../../service/AdminService/courseService";
import PageLoader from "../../../components/global/Loader/PageLoader";
import { tabList } from "../../../constants/constants";

const CourseReport = () => {

  const tableRef = useRef(null);
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const defaultStatus = searchParams.get("status") || "active";

  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isInputHovered, setIsInputHovered] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [status, setStatus] = useState(defaultStatus);
  const isActive = status === "active";

  const {
    currentPage,
    pageSize,
    setCurrentPage,
    totalElements,
    setTotalElements,
  } = usePagination();

  const { data, fetchCourseKpiReport } = useCourseReport();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchCourseKpiReport(
        currentPage - 1,
        pageSize,
        isActive,
        searchText
      );

      if (response) {
        setTotalElements(response.totalElements);
      }
    } catch (error) {
      setTotalElements(0);
    } finally {
      setLoading(false);
    }

  };

  useEffect(() => {
    const getData = setTimeout(() => {
      const query = searchText?.trim() || "";
      fetchData(currentPage, pageSize, isActive, query);
    }, 1000);
    return () => clearTimeout(getData);
  }, [searchText, currentPage, pageSize, isActive]);

  const handleViewCourseClick = (record) => {
    navigate(`/books/course-report/${record.courseId}`);
  };

  const handleSearch = (value) => {
    setSearchText(value.toLowerCase());
    setCurrentPage(1);
  };


  const downloadReport = async (isActive, searchText) => {
    setLoader(true);
    try {
      await downloadCourseKpiReport(isActive, searchText);
      showSuccessMessage("Report Downloaded Successfully!");
    } catch (error) {
      showErrorMessage("Failed to download report");
    } finally {
      setLoader(false);
    }
  }

  const handleTableChange = (pagination) => {
    if (pagination) {
      if (pagination.pageSize !== pageSize) {
        setCurrentPage(1);
      } else {
        setCurrentPage(pagination.current);
      }
      const scrollableNode = tableRef.current?.querySelector(".ant-table-body");
      if (scrollableNode) {
        scrollableNode.scrollTo({ top: 0, behavior: "auto" });
      }
    }
  };

  const handleTabChange = (key) => {
    setStatus(key);
    setCurrentPage(1);
    setSearchText(""); 
  };

  return (
    <>
      <PageLoader visible={loader} />
      <section className="w-[100%] max-w-full h-[95vh] p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)]">
        <header className="flex flex-col gap-6 mb-6">
          <div className="flex justify-between items-center h-[50px]">
            <h1 className="text-[#E74860] text-[1.75rem] font-normal">
              All Courses Report
            </h1>
          </div>
        </header>

        <Row>
          <Col span={6}>
            <CustomTab
              entries={tabList}
              activeKey={status}
              onChange={handleTabChange}
            />
          </Col>
          <Col span={18}>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <div
                onMouseEnter={() => setIsInputHovered(true)}
                onMouseLeave={() => setIsInputHovered(false)}
              >
                <CustomInput
                  key={status}
                  icon={
                    isInputHovered ? (
                      <LottieIcon name="searchIcon" size={24} shouldPlay={true} />
                    ) : (
                      <img src={searchIconsvg} alt="search" className="w-6 h-6" />
                    )
                  }
                  className="!w-[250px]"
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search Course"
                  allowClear
                />
              </div>
              <CustomButton
                onClick={() => downloadReport(isActive, searchText)}
              >
                Export
              </CustomButton>
            </div>
          </Col>
        </Row>

        <div ref={tableRef}>
          <CommonTable
            data={data}
            columns={courseReportColumns()}
            showLoader={loading}
            className="hide-scrollbar top-table-radius"
            scroll={{ y: "60vh" }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalElements,
              showSizeChanger: true,
            }}
            onChange={handleTableChange}
            rowClickable={true}
            settingModalDataFunction={handleViewCourseClick}
            hover={true}
            rowKey={(record) => record.courseId}
            serverSide={true}
          />
        </div>
      </section>
    </>
  );
};

export default AdminHOC(CourseReport);
